import React, { useMemo } from "react";
import styled from "styled-components";

import { INK_STAMP_IMG } from "./assetMap";
import { Backdrop } from "./types";

/**
 * Ink stamp asset + circular name along the top arc. The stamp PNGs already
 * include the gears and bottom "★ HACK THE NORTH 2026 ★" — we only overlay
 * the dynamic name, centered at 12 o'clock and scaled to fit the arc.
 */

export const INK_NAME_FONT =
  "Satoshi, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/** Upper-arc path in a 100×100 viewBox (matches the stamp's text ring). */
export const INK_NAME_ARC_BASE_Y = 52;
export const INK_NAME_ARC_RADIUS = 32;
/** Matches SVG `letter-spacing` on the name overlay. */
export const INK_NAME_LETTER_SPACING_EM = 0.04;

export const inkNameArcPath = (yOffsetPct: number): string => {
  const y = INK_NAME_ARC_BASE_Y + yOffsetPct;
  return `M 18,${y} A ${INK_NAME_ARC_RADIUS},${INK_NAME_ARC_RADIUS} 0 0 1 82,${y}`;
};

/** Radius of that arc as a fraction of stamp diameter (for canvas export). */
export const INK_NAME_ARC_RADIUS_FRAC = INK_NAME_ARC_RADIUS / 100;

/**
 * Arc center Y relative to stamp center, as a fraction of diameter.
 * SVG arc is centered at (50, BASE_Y + yOffset), stamp center at (50, 50).
 */
export const inkNameArcCenterYFrac = (yOffsetPct: number): number =>
  (INK_NAME_ARC_BASE_Y + yOffsetPct - 50) / 100;

/** How wide a swath of the top arc the name may occupy (radians). */
export const INK_NAME_ARC_SPAN = (110 * Math.PI) / 180;

let inkPathSeq = 0;

export const inkNameFontSizePx = (name: string, diameterPx: number): number => {
  const chars = Math.max(name.length, 1);
  // Empirically fits ~7-letter names at ~0.085× diameter; shrink for longer.
  const base = diameterPx * 0.085;
  const scaled = base * Math.min(1, 7 / chars);
  return Math.max(diameterPx * 0.045, scaled);
};

interface InkStampLayerProps {
  backdrop: Backdrop;
  name: string;
  /** Diameter in native art px */
  size: number;
  /** Ink colour for the name overlay */
  color: string;
  fontWeight: number;
  /** Shift name down, % of diameter (viewBox units) */
  nameYOffsetPct: number;
  rotationDeg: number;
  left: number;
  top: number;
}

const InkStampLayer: React.FC<InkStampLayerProps> = ({
  backdrop,
  name,
  size,
  color,
  fontWeight,
  nameYOffsetPct,
  rotationDeg,
  left,
  top,
}) => {
  const pathId = useMemo(() => {
    inkPathSeq += 1;
    return `ink-name-arc-${inkPathSeq}`;
  }, []);
  const displayName = name.trim().toUpperCase();
  const fontSize = inkNameFontSizePx(displayName || " ", 100); // viewBox units
  const arcPath = inkNameArcPath(nameYOffsetPct);

  return (
    <Wrap
      style={{
        left,
        top,
        width: size,
        height: size,
        transform: `rotate(${rotationDeg}deg)`,
      }}
    >
      <StampImg
        src={INK_STAMP_IMG[backdrop]}
        alt=""
        aria-hidden
        draggable={false}
      />
      {displayName ? (
        <NameSvg viewBox="0 0 100 100" aria-hidden>
          <defs>
            <path id={pathId} d={arcPath} fill="none" />
          </defs>
          <text
            fill={color}
            fontFamily={INK_NAME_FONT}
            fontWeight={fontWeight}
            fontSize={fontSize}
            letterSpacing={`${INK_NAME_LETTER_SPACING_EM}em`}
          >
            <textPath
              href={`#${pathId}`}
              // eslint-disable-next-line react/no-unknown-property
              xlinkHref={`#${pathId}`}
              startOffset="50%"
              textAnchor="middle"
            >
              {displayName}
            </textPath>
          </text>
        </NameSvg>
      ) : null}
    </Wrap>
  );
};

const Wrap = styled.div`
  position: absolute;
  pointer-events: none;
  user-select: none;
  z-index: 3;
`;

const StampImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const NameSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
`;

export default InkStampLayer;
